<template lang="pug">
  div
    div.text-center.py-4(v-if="loading")
      b-spinner(small)
    template(v-else)
      p.text-muted.small.mb-2 {{$t('mdm_page.profile_apps_help')}}
      b-table.list-stacked.mdm-profile-app-table(
        :fields="fields",
        :items="rows",
        show-empty,
        :empty-text="$t('mdm_page.no_app')",
        hover,
        fixed,
        stacked="md")
        //- 앱 이름 — 길면 … 말줄임(전체 이름은 title 툴팁)
        template(v-slot:cell(app_name)="row")
          span.mdm-app-name(:title="row.item.app_name") {{row.item.app_name}}
        //- 설치 정책 — view: 뱃지 / edit: 드롭다운
        template(v-slot:cell(state)="row")
          template(v-if="mode === 'view'")
            b-badge(v-if="row.item.value === 'install'", variant="success") {{$t('mdm_page.state_install')}}
            b-badge(v-else-if="row.item.value === 'uninstall'", variant="danger") {{$t('mdm_page.state_uninstall')}}
            span.text-muted(v-else) {{$t('mdm_page.state_none')}}
          b-form-select.mdm-profile-app-state(
            v-else,
            v-model="row.item.value",
            :options="stateOptions",
            size="sm")
</template>

<script>
// 구성 상세 "애플리케이션" 탭 — 이 구성에 앱 설치 정책 설정(구성 중심).
// 조회·저장 = GET/PUT /mdm/policy-profiles/{pk}/applications (전 앱 조회 · 변경분 벌크)
import { MdmProfileAPI } from "@/api/mdm"

export default {
  name: "MdmProfileAppPolicyTab",
  props: {
    // 편집 중인 구성 PK (변경 시 watch 재로딩)
    profilePk: { type: Number, default: null },
    // "edit" | "view" — view면 읽기전용
    mode: { type: String, default: "edit" },
  },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [], // [{ key, application_fk, app_name, version, original, value }]
      stateOptions: [
        { value: "install", text: this.$t("mdm_page.state_install") },
        { value: "none", text: this.$t("mdm_page.state_none") },
        { value: "uninstall", text: this.$t("mdm_page.state_uninstall") },
      ],
      fields: [
        { key: "app_name", label: this.$t("mdm_page.app_name"), thStyle: { width: "50%" } },
        { key: "version", label: this.$t("version"), class: "text-center", thStyle: { width: "22%" } },
        { key: "state", label: this.$t("mdm_page.install_policy"), class: "text-center", thStyle: { width: "28%" } },
      ],
    }
  },
  methods: {
    setBusy(busy) {
      // 상위 모달 [저장] disable 제어 (단방향 통지)
      this.$emit("busy-change", busy)
    },
    async load() {
      if (!this.profilePk) return
      this.loading = true
      this.setBusy(true)
      try {
        const res = await MdmProfileAPI.get(`${this.profilePk}/applications`)
        const apps = res.data.objects || []
        this.rows = apps.map(a => {
          const state = this.actionToState(a.action)
          return {
            key: a.application_fk,
            application_fk: a.application_fk,
            app_name: a.app_name,
            version: a.version,
            original: state,
            value: state,
          }
        })
      } catch (error) {
        // 조회 실패 토스트는 전역 인터셉터가 처리
        this.rows = []
      } finally {
        this.loading = false
        this.setBusy(false)
      }
    },
    actionToState(action) {
      if (action === "INSTALL") return "install"
      if (action === "UNINSTALL") return "uninstall"
      return "none"
    },
    stateToAction(state) {
      return state === "install" ? "INSTALL" : "UNINSTALL"
    },
    // null = 설치 안 함(행 제거)
    buildTarget(row) {
      return {
        application_fk: row.application_fk,
        action: row.value === "none" ? null : this.stateToAction(row.value),
      }
    },
    // 상위 모달 [저장]이 ref로 호출
    async save() {
      if (this.saving) return
      const changes = this.rows.filter(r => r.value !== r.original)
      if (!changes.length) {
        this.$emit("saved")
        return
      }
      this.saving = true
      this.setBusy(true)
      try {
        // 변경분만 벌크 저장
        await MdmProfileAPI.put(`${this.profilePk}/applications`, {
          targets: changes.map(r => this.buildTarget(r)),
        })
        // 재저장 시 no-op 되도록 original 갱신
        changes.forEach(r => { r.original = r.value })
        this.$awn.success(this.$t("mdm_page.profile_apps_saved"))
        this.$emit("saved")
      } catch (error) {
        // 실패 시 전역 인터셉터가 토스트 → 모달 유지해 재시도
      } finally {
        this.saving = false
        this.setBusy(false)
      }
    },
  },
  watch: {
    // 재오픈으로 구성 바뀌면 재로딩 (immediate=최초 로드)
    profilePk: { immediate: true, handler() { void this.load() } },
  },
}
</script>

<style lang="scss">
// 이 탭 테이블 전용 (조상 의존 X)
.mdm-profile-app-table {
  td {
    vertical-align: middle;
  }

  // 드롭다운 중앙 배치 (컬럼 text-center와 맞춤)
  .mdm-profile-app-state {
    max-width: 180px;
    margin: 0 auto;
  }

  // 앱 이름 … 말줄임
  .mdm-app-name {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // 데스크탑: 첫 컬럼 좌측 여백 (모바일 카드는 미적용)
  @media (min-width: 768px) {
    th:first-child,
    td:first-child {
      padding-left: 1rem;
    }
  }

  // 모바일 stacked 카드: 라벨|값 2열 정렬 (§10 표준)
  @media (max-width: 767px) {
    &.b-table-stacked-md > tbody > tr > td {
      display: grid !important;
      // minmax(0,1fr): 긴 앱 이름이 카드 밀지 않고 말줄임되게 (1fr 기본은 min-width auto)
      grid-template-columns: 6em minmax(0, 1fr);
      align-items: center;
      column-gap: 0.5rem;
      max-width: 340px;
      margin-left: auto !important;
      margin-right: auto !important;

      &::before {
        float: none !important;
        width: auto !important;
        text-align: right !important;
        padding: 0 !important;
      }

      &::after {
        display: none !important;
      }

      > div {
        width: auto !important;
        padding: 0 !important;
        text-align: left;
      }
    }

    .mdm-profile-app-state {
      max-width: 220px;
    }
  }
}
</style>
